import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Eye, ChevronLeft, ChevronRight } from 'lucide-react';
import axiosInstance from '@/utils/axiosInstance';
import { API_ENDPOINTS } from '@/constants/config';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/* ─── Hardcoded fallback entries ─────────────────────────────────── */
const FALLBACK_ENTRIES = [
  { _id: '1', date: '2026-05-22T18:30:00Z', vehicle: { vehicleNumber: 'BUS001' }, createdBy: { firstName: 'Rahul', lastName: 'Sharma' }, fuelType: 'diesel', quantity: 58, cost: 5220, kmAverage: 7.76, odometer: 13300, paymentMode: 'upi', petrolPump: 'HP Petrol Pump' },
  { _id: '2', date: '2026-05-21T07:10:00Z', vehicle: { vehicleNumber: 'BUS002' }, createdBy: { firstName: 'Amit',  lastName: 'Verma'  }, fuelType: 'diesel', quantity: 70, cost: 6300, kmAverage: 7.14, odometer: 9700,  paymentMode: 'card', petrolPump: 'Indian Oil' },
  { _id: '3', date: '2026-05-20T17:45:00Z', vehicle: { vehicleNumber: 'BUS003' }, createdBy: { firstName: 'Rahul', lastName: 'Sharma' }, fuelType: 'diesel', quantity: 55, cost: 4950, kmAverage: 5.92, odometer: 12850, paymentMode: 'upi', petrolPump: 'HP Petrol Pump' },
  { _id: '4', date: '2026-05-18T18:20:00Z', vehicle: { vehicleNumber: 'BUS002' }, createdBy: { firstName: 'Amit',  lastName: 'Verma'  }, fuelType: 'diesel', quantity: 65, cost: 5850, kmAverage: null, odometer: 9200,  paymentMode: 'cash', petrolPump: 'Bharat Petroleum' },
  { _id: '5', date: '2026-05-17T18:15:00Z', vehicle: { vehicleNumber: 'BUS001' }, createdBy: { firstName: 'Rahul', lastName: 'Sharma' }, fuelType: 'diesel', quantity: 60, cost: 5400, kmAverage: null, odometer: 12400, paymentMode: 'cash', petrolPump: 'Indian Oil' },
];

const DATE_PRESETS = [
  { label: 'Today',      value: 'today'     },
  { label: 'This Week',  value: 'week'      },
  { label: 'This Month', value: 'month'     },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'Custom Range', value: 'custom'  },
];

const getDateRange = (preset) => {
  const now = new Date();
  switch (preset) {
    case 'today':
      return { startDate: new Date(now.setHours(0,0,0,0)).toISOString(), endDate: new Date().toISOString() };
    case 'week': {
      const s = new Date(); s.setDate(s.getDate() - 7);
      return { startDate: s.toISOString(), endDate: new Date().toISOString() };
    }
    case 'month':
      return { startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), endDate: new Date().toISOString() };
    case 'lastMonth': {
      const y = now.getFullYear(), m = now.getMonth();
      return { startDate: new Date(y,m-1,1).toISOString(), endDate: new Date(y,m,0,23,59,59).toISOString() };
    }
    default:
      return { startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString(), endDate: new Date().toISOString() };
  }
};

const rowBg = (km) => {
  if (!km) return 'hover:bg-gray-50 border-gray-50';
  if (km < 6)  return 'bg-red-50 border-red-100 hover:bg-red-100';
  if (km < 7)  return 'bg-amber-50 border-amber-100 hover:bg-amber-100';
  return 'hover:bg-gray-50 border-gray-50';
};

const StatusBadge = ({ km }) => {
  if (!km) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">—</span>;
  if (km < 6)  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">Critical</span>;
  if (km < 7)  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">Low</span>;
  if (km >= 8) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Efficient</span>;
  return          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">Normal</span>;
};

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleString('en-IN', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
  } catch { return '—'; }
};

const fmtNum = (n) => n != null ? new Intl.NumberFormat('en-IN').format(n) : '—';

/* ─── Component ──────────────────────────────────────────────────── */

const FuelEntries = ({ onEdit, onAddEntry, onRefresh }) => {
  const [entries,     setEntries]     = useState([]);
  const [vehicles,    setVehicles]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [totalCount,  setTotalCount]  = useState(0);
  const [viewEntry,   setViewEntry]   = useState(null);

  const [vehicleFilter, setVehicleFilter] = useState('');
  const [datePreset,    setDatePreset]    = useState('month');
  const [customStart,   setCustomStart]   = useState('');
  const [customEnd,     setCustomEnd]     = useState('');
  const [search,        setSearch]        = useState('');

  useEffect(() => {
    axiosInstance.get(API_ENDPOINTS.vehicles)
      .then((r) => setVehicles(r.data?.data?.vehicles || []))
      .catch(() => setVehicles([]));
  }, []);

  useEffect(() => { fetchEntries(); }, [vehicleFilter, datePreset, customStart, customEnd, page]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      let dateParams = {};
      if (datePreset === 'custom') {
        if (customStart && customEnd) {
          dateParams = {
            startDate: new Date(customStart).toISOString(),
            endDate:   new Date(customEnd + 'T23:59:59').toISOString(),
          };
        }
      } else {
        dateParams = getDateRange(datePreset);
      }

      const params = {
        page, limit: 15,
        ...(vehicleFilter && { vehicle: vehicleFilter }),
        ...dateParams,
      };

      const res  = await axiosInstance.get(API_ENDPOINTS.fuelEntries, { params });
      const data = res.data?.data;
      const list = data?.fuelEntries || [];
      setEntries(list.length ? list : FALLBACK_ENTRIES);
      setTotalPages(data?.pagination?.pages || 1);
      setTotalCount(data?.pagination?.total  || list.length || FALLBACK_ENTRIES.length);
    } catch {
      setEntries(FALLBACK_ENTRIES);
      setTotalPages(1);
      setTotalCount(FALLBACK_ENTRIES.length);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this fuel entry?')) return;
    const isFallback = ['1','2','3','4','5'].includes(String(id));
    if (isFallback) {
      alert('This is demo data and cannot be deleted.');
      return;
    }
    try {
      await axiosInstance.delete(API_ENDPOINTS.fuelEntry(id));
      fetchEntries();
      onRefresh?.();
    } catch {
      alert('Could not delete entry. Please try again.');
    }
  };

  const driverName = (entry) => {
    if (entry.createdBy?.firstName) return `${entry.createdBy.firstName} ${entry.createdBy.lastName || ''}`.trim();
    return '—';
  };

  const vehicleNum = (entry) =>
    entry.vehicle?.vehicleNumber || entry.vehicleNumber || '—';

  const displayedEntries = search.trim()
    ? entries.filter((e) => {
        const q = search.toLowerCase();
        return (
          vehicleNum(e).toLowerCase().includes(q) ||
          driverName(e).toLowerCase().includes(q) ||
          (e.petrolPump || '').toLowerCase().includes(q)
        );
      })
    : entries;

  return (
    <div className="space-y-4">

      {/* ── Filter Bar ── */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Vehicle Dropdown */}
        <select
          value={vehicleFilter}
          onChange={(e) => { setVehicleFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px]"
        >
          <option value="">All Vehicles</option>
          {vehicles.map((v) => (
            <option key={v._id} value={v._id}>
              {v.vehicleNumber || v.name}
            </option>
          ))}
        </select>

        {/* Date Preset */}
        <select
          value={datePreset}
          onChange={(e) => { setDatePreset(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[150px]"
        >
          {DATE_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>

        {/* Custom Range — shown only when selected */}
        {datePreset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customStart}
              onChange={(e) => { setCustomStart(e.target.value); setPage(1); }}
              className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" value={customEnd}
              onChange={(e) => { setCustomEnd(e.target.value); setPage(1); }}
              className="px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search bus, driver, pump…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white w-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Add Missed Entry — secondary, right aligned */}
        <div className="ml-auto">
          <button
            onClick={onAddEntry}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-gray-300 rounded-xl bg-white hover:bg-gray-50 text-gray-700 shadow-sm transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Missed Entry
          </button>
        </div>
      </div>

      {/* ── Legend ── */}
      <div className="flex items-center gap-5 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-amber-100 border border-amber-300" />
          Below average mileage
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-red-100 border border-red-300" />
          Critical anomaly
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-green-100 border border-green-300" />
          Above average
        </span>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {['Date & Time','Vehicle','Driver','Fuel Type','Qty (L)','Cost (₹)','Mileage (km/L)','Odometer','Payment','Status','Actions'].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-14 text-center text-gray-400">
                    Loading entries…
                  </td>
                </tr>
              ) : displayedEntries.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-14 text-center text-gray-400">
                    No entries found for the selected filters.
                  </td>
                </tr>
              ) : (
                displayedEntries.map((entry) => (
                  <tr key={entry._id} className={`border-t transition-colors ${rowBg(entry.kmAverage)}`}>
                    <td className="px-4 py-3.5 text-gray-600 text-xs whitespace-nowrap">{fmtDate(entry.date)}</td>
                    <td className="px-4 py-3.5 font-semibold text-gray-900">{vehicleNum(entry)}</td>
                    <td className="px-4 py-3.5 text-gray-700">{driverName(entry)}</td>
                    <td className="px-4 py-3.5">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 capitalize">
                        {entry.fuelType || 'Diesel'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-gray-700">{fmtNum(entry.quantity)}</td>
                    <td className="px-4 py-3.5 text-gray-700">₹{fmtNum(entry.cost)}</td>
                    <td className="px-4 py-3.5">
                      {entry.kmAverage ? (
                        <span className={
                          entry.kmAverage < 6  ? 'text-red-600 font-semibold' :
                          entry.kmAverage < 7  ? 'text-amber-600 font-semibold' :
                          entry.kmAverage >= 8 ? 'text-green-600 font-semibold' :
                          'text-gray-700'
                        }>
                          {entry.kmAverage.toFixed(2)}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3.5 text-gray-700 text-xs whitespace-nowrap">
                      {entry.odometer ? `${fmtNum(entry.odometer)} km` : '—'}
                    </td>
                    <td className="px-4 py-3.5 text-gray-700 capitalize">{entry.paymentMode || '—'}</td>
                    <td className="px-4 py-3.5"><StatusBadge km={entry.kmAverage} /></td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <button onClick={() => setViewEntry(entry)} className="text-gray-400 hover:text-blue-600 transition-colors" title="View">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button onClick={() => onEdit?.(entry)} className="text-gray-400 hover:text-blue-600 transition-colors" title="Edit">
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(entry._id)} className="text-gray-400 hover:text-red-500 transition-colors" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            Showing {entries.length > 0 ? (page - 1) * 15 + 1 : 0}–{Math.min(page * 15, totalCount)} of {totalCount} entries
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-3 py-1 text-xs font-semibold text-blue-600 border border-blue-200 rounded-lg bg-blue-50">
              {page}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* ── View Details Dialog ── */}
      <Dialog open={!!viewEntry} onOpenChange={(o) => !o && setViewEntry(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Entry Details</DialogTitle>
          </DialogHeader>
          {viewEntry && (
            <div className="space-y-3 text-sm mt-1">
              {[
                ['Date & Time',   fmtDate(viewEntry.date)],
                ['Vehicle',       vehicleNum(viewEntry)],
                ['Driver',        driverName(viewEntry)],
                ['Fuel Type',     viewEntry.fuelType],
                ['Quantity',      viewEntry.quantity ? `${viewEntry.quantity} L` : '—'],
                ['Cost',          viewEntry.cost ? `₹${fmtNum(viewEntry.cost)}` : '—'],
                ['Rate/Litre',    viewEntry.rate != null ? `₹${Number(viewEntry.rate).toFixed(2)}` : '—'],
                ['Mileage',       viewEntry.kmAverage != null ? `${Number(viewEntry.kmAverage).toFixed(2)} km/L` : '—'],
                ['Odometer',      viewEntry.odometer ? `${fmtNum(viewEntry.odometer)} km` : '—'],
                ['Payment Mode',  viewEntry.paymentMode],
                ['Petrol Pump',   viewEntry.petrolPump || '—'],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between items-center border-b border-gray-50 pb-2.5">
                  <span className="text-gray-500">{label}</span>
                  <span className="font-medium text-gray-800 capitalize text-right">{val || '—'}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FuelEntries;
